import React, { useCallback, useEffect, useRef, useState } from 'react';
import ModalPortal from '../ModalPortal';
import { C } from './estilos';
import { useBotAjustes } from './useBotAjustes';
import { SeccionSwitches } from './ListaAjustes';
import { PanelCambio } from './PanelCambio';
import { DestinatarioAvisos } from './DestinatarioAvisos';
import { AllowlistBot } from './AllowlistBot';
import { PanelPlantilla, SeccionPlantillas, usePlantillasBot } from './PlantillasWhatsApp';

/**
 * "Ajustes del bot" · maestro-detalle (diseño v3 PEICAR).
 *
 * Izquierda: switches por sección, los bloques que sí guardan (a quién le
 * contesta, a quién le avisa) y las plantillas de WhatsApp.
 * Derecha: el ejemplo prendido/apagado del switch elegido, o cómo se ve la
 * plantilla elegida.
 *
 * Todo se guarda: los switches en /bot-ventas/switches (aplican en ≤ 60 s) y
 * los bloques en /bot-ventas/config. Ver useBotAjustes.
 */

const ANCHO_ANGOSTO = 960;

const leerAngosto = () => (typeof window !== 'undefined' ? window.innerWidth < ANCHO_ANGOSTO : false);

export const useAnchoAngosto = () => {
  const [angosto, setAngosto] = useState(leerAngosto);
  useEffect(() => {
    const onResize = () => setAngosto(leerAngosto());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return angosto;
};

export const Contador = ({ texto, valor, color, borde }) => (
  <span style={{ fontSize: 12, border: `1px solid ${borde}`, color, borderRadius: 10, padding: '2px 10px', whiteSpace: 'nowrap' }}>
    {texto} <b>{valor}</b>
  </span>
);

export const Toast = ({ texto }) => (
  <div
    role="status"
    style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1450,
      background: C.navy, color: '#fff', borderRadius: 14, padding: '11px 16px', fontSize: 13.5,
      display: 'flex', gap: 10, alignItems: 'center', boxShadow: '0 10px 30px rgba(12,32,54,.3)', maxWidth: 'calc(100vw - 32px)',
    }}
  >
    <i className="pi pi-check-circle" style={{ color: C.menta }} />{texto}
  </div>
);

const BLOQUES = {
  allowlist: AllowlistBot,
  destinatario: DestinatarioAvisos,
};

export const BotAjustesModal = ({ onClose }) => {
  const { cargando, error, guardando, secciones, porClave, cambios, valorDe, servidorDe, cambiar, regresar, cambiados } = useBotAjustes();
  const plantillas = usePlantillasBot();
  const angosto = useAnchoAngosto();

  // Lo elegido a la izquierda: un switch o una plantilla.
  const [elegido, setElegido] = useState(() => ({ tipo: 'switch', clave: secciones[0]?.switches[0]?.clave || null }));
  const [toast, setToast] = useState(null);
  const dialogRef = useRef(null);

  const avisar = useCallback((texto) => setToast({ texto, id: Date.now() }), []);

  // El toast se va solo; el timer se limpia si cambia o si se cierra el modal.
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const elegirSwitch = useCallback((clave) => setElegido({ tipo: 'switch', clave }), []);
  const elegirPlantilla = useCallback((clave) => setElegido({ tipo: 'plantilla', clave }), []);

  const onToggle = useCallback(async (clave) => {
    elegirSwitch(clave);
    if (!porClave[clave]?.editable) return;
    const r = await cambiar(clave, !valorDe(clave));
    if (r.ok) avisar('Guardado · aplica en 1 minuto o menos');
    else if (r.mensaje) avisar(r.mensaje);
  }, [porClave, valorDe, cambiar, avisar, elegirSwitch]);

  const onRegresar = useCallback(async (clave) => {
    elegirSwitch(clave);
    const r = await regresar(clave);
    if (r.ok) avisar('Regresó al valor del servidor');
    else if (r.mensaje) avisar(r.mensaje);
  }, [regresar, avisar, elegirSwitch]);

  const switchElegido = elegido.tipo === 'switch' ? porClave[elegido.clave] : null;
  const plantillaElegida = elegido.tipo === 'plantilla'
    ? plantillas.plantillas.find((p) => p.clave === elegido.clave)
    : null;
  const botApagado = !valorDe('ENABLED');

  const columna = angosto
    ? { display: 'flex', flexDirection: 'column', gap: 14 }
    : { display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0, paddingRight: 4 };

  return (
    <ModalPortal>
      <div
        role="presentation"
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        style={{
          position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: angosto ? 0 : 16, background: 'rgba(12,32,54,.52)',
        }}
      >
        <div
          ref={dialogRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label="Ajustes del bot"
          style={{
            width: angosto ? '100%' : 'min(1440px, 100%)', height: angosto ? '100%' : 'calc(100vh - 32px)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden', outline: 'none',
            borderRadius: angosto ? 0 : 20, background: C.fondo, boxShadow: '0 24px 60px rgba(12,32,54,.32)',
          }}
        >
          {/* Encabezado */}
          <div style={{ background: '#fff', borderBottom: `1px solid ${C.linea}`, padding: '14px 18px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <h2 style={{ margin: 0, fontSize: 21, fontWeight: 300, color: C.steel }}>
                <b style={{ fontWeight: 700 }}>Bot de cotizaciones.</b> Ajustes
              </h2>
              <p style={{ margin: 0, fontSize: 13.5, color: C.ink3, lineHeight: 1.45 }}>
                Elige un ajuste y a la derecha verás qué cambia con él: cómo queda la conversación prendido y apagado.
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Contador texto="Cambiados en panel" valor={cambiados} color={C.steel} borde={C.cyanBorde} />
              </div>
            </div>
            <button
              type="button"
              aria-label="Cerrar"
              title="Cerrar"
              onClick={onClose}
              style={{ width: 34, height: 34, border: 'none', borderRadius: 10, background: '#F1F5F9', color: C.ink3, cursor: 'pointer', flexShrink: 0 }}
            >
              <i className="pi pi-times" />
            </button>
          </div>

          {(cargando || error) && (
            <div role={error ? 'alert' : 'status'} style={{ background: error ? '#FBE7E7' : C.cyanTinte, borderBottom: `1px solid ${error ? '#F2CACA' : C.linea}`, color: error ? '#B91C1C' : C.steel, fontSize: 12.5, fontWeight: 600, padding: '8px 18px', display: 'flex', gap: 8, alignItems: 'center' }}>
              <i className={error ? 'pi pi-exclamation-triangle' : 'pi pi-spin pi-spinner'} style={{ fontSize: 12 }} />
              {error || 'Cargando los ajustes del bot…'}
            </div>
          )}

          {/* Cuerpo: dos columnas con scroll propio; en angosto, una sola columna. */}
          <div
            style={angosto
              ? { flex: 1, minHeight: 0, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 14 }
              : { flex: 1, minHeight: 0, padding: 16, display: 'grid', gridTemplateColumns: 'minmax(0, 1.35fr) minmax(0, 1fr)', gap: 16 }}
          >
            <div style={columna}>
              {secciones.map((s) => {
                const Bloque = s.bloque ? BLOQUES[s.bloque] : null;
                return (
                  <SeccionSwitches
                    key={s.id}
                    seccion={s}
                    valorDe={valorDe}
                    servidorDe={servidorDe}
                    guardando={guardando}
                    bloqueado={cargando || Boolean(error)}
                    cambios={cambios}
                    elegido={elegido.tipo === 'switch' ? elegido.clave : null}
                    onElegir={elegirSwitch}
                    onToggle={onToggle}
                    onRegresar={onRegresar}
                  >
                    {Bloque && <Bloque />}
                  </SeccionSwitches>
                );
              })}
              <SeccionPlantillas
                cargando={plantillas.cargando}
                plantillas={plantillas.plantillas}
                error={plantillas.error}
                elegida={elegido.tipo === 'plantilla' ? elegido.clave : null}
                onElegir={elegirPlantilla}
              />
            </div>
            <div style={columna}>
              {plantillaElegida
                ? <PanelPlantilla plantilla={plantillaElegida} />
                : <PanelCambio item={switchElegido} on={switchElegido ? valorDe(switchElegido.clave) : false} botApagado={botApagado} />}
            </div>
          </div>
        </div>

        {toast && <Toast key={toast.id} texto={toast.texto} />}
      </div>
    </ModalPortal>
  );
};

export default BotAjustesModal;
